import keymirror from 'keymirror'
import { makeMethaneAppEntityName } from 'app/MissionControlMethaneSolution/helpers/common'
import { EmissionRateUnitType } from '../types/graphql'
import { MeasurementType, TimeUnit, UnitSystem } from '../types/common'

export const TAB_KEY = 'tab'

const TAB_DETECTION_SUFFIX = `:${TAB_KEY}/:id?`

export const INBOX_URL = `/inbox`
export const WIP_URL = `/wip`
export const DETECTIONS_URL = `/detections`
export const ASSETS_URL = `/assets`
export const RECONCILIATION_URL = `/reconciliation`
export const OGMP2_URL = `/OGMP2`
export const AUDIT_URL = `/audit`
export const DASHBOARD_URL = '/dashboards'
export const CO2E_URL = '/CO2e'
export const EMISSION_OBSERVATIONS_URL = '/emissionObservations'
export const EMISSIONS_URL = '/emissions'
export const EMISSION_URL = '/emissionEvents'

const YEAR_MONTH_OPTIONAL_PATH_PARAMS = `/:year?/:month?/`

const appendYearMonthPathParams = (url: string) =>
  `${url}${YEAR_MONTH_OPTIONAL_PATH_PARAMS}`

export const APP_URLS = {
  EMISSIONS: EMISSIONS_URL,
  EMISSIONS_BY_ASSET_SITE: `${EMISSIONS_URL}/:assetReference/:emissionEventId?`,
  EMISSION: `${EMISSION_URL}/:id`,
  EMISSION_OBSERVATIONS: `${EMISSION_OBSERVATIONS_URL}/:id`,
  INBOX: INBOX_URL,
  INBOX_EMISSIONS_BY_ASSET: `${INBOX_URL}/:assetReference/:emissionObservationId?`,
  WIP: WIP_URL,
  WIP_TAB: `${WIP_URL}/${TAB_DETECTION_SUFFIX}`,
  ASSETS: ASSETS_URL,
  ASSET_VIEW: `${ASSETS_URL}/:${TAB_KEY}`,
  RECONCILIATION_BASE: RECONCILIATION_URL,
  RECONCILIATION: appendYearMonthPathParams(RECONCILIATION_URL),
  RECONCILIATION_DECISION_LOG: appendYearMonthPathParams(
    `${RECONCILIATION_URL}/decisions`
  ),
  RECONCILIATION_SITE_REPORT: `${RECONCILIATION_URL}/:siteId/:year/:month/:day?/:equipmentId?`,
  AUDIT_BASE: AUDIT_URL,
  AUDIT: appendYearMonthPathParams(AUDIT_URL),
  CO2E_BASE: CO2E_URL,
  CO2E: appendYearMonthPathParams(CO2E_URL),
  OGMP2_BASE: OGMP2_URL,
  OGMP2: appendYearMonthPathParams(OGMP2_URL),
  OGMP2_BY_SITE: `${OGMP2_URL}/assets/:assetReference/:year/:month?/`,
}

export const ITEMS_PER_PAGE = 20

export const LOAD_MORE_ITEMS_PER_PAGE = 100

export const NO_VALUE_PLACEHOLDER = '−−'

export const METHANE_OLD_ENTITY_NAMES = keymirror({
  deep_dive: null,
  reconciliation_overview: null,
  decisionLog: null,
  inbox_open_emissions: null,
  inbox_open_emissions_grouped: null,
  detection_wip: null,
  detection_wip_closed: null,
  audit: null,
  executive_dashboard: null,
  co2e: null,
  flyover_dashboard: null,
  emission_event: null,
  emission_event_site_events: null,
  emission_event_site_observations: null,
  dashboards_detections_dashboard: null,
  OGMP2_assets: null,
  OGMP2_report: null,
  OGMP2_by_asset_measurements: null,
})

/** Creating new entity names with prefix */
export const METHANE_ENTITY_NAMES = [
  ...Object.values(METHANE_OLD_ENTITY_NAMES),
  'asset',
].reduce(
  (acc, entityName) => ({
    ...acc,
    // Giving a prefix to each entity
    [entityName]: makeMethaneAppEntityName(entityName),
  }),
  {} as typeof METHANE_OLD_ENTITY_NAMES & { asset: string }
)

/** 14 Nov 2023: Hiding some unfinished features before the MVP release */
export const IS_FEATURE_ENABLED = {
  WIP_EVENT_ACTIVITY: false,
}

export const DETECTION_TYPES = keymirror({
  DETECTION_TYPE_CMS: null,
  DETECTION_TYPE_FLYOVER: null,
  DETECTION_TYPE_OGI: null,
  DETECTION_TYPE_SATELLITE: null,
  DETECTION_TYPE_UNKNOWN: null,
  DETECTION_TYPE_MANUAL: null,
  DETECTION_TYPE_DRONE: null,
  DETECTION_TYPE_OWD: null,
  DETECTION_TYPE_VENTING_FLARING_BLOWDOWN: null,
  NO_DETECTION_TYPE: null,
})

export const SUPER_EMITTERS_EMISSION_RATE_THRESHOLD = 100

export const OPS_COMMENT_DEFAULT_ACTION_NAME = 'Ops Comment'

export const EMISSIONS_UNIT_LABEL = {
  [EmissionRateUnitType.KilogramsPerHour]: 'kg/h',
}

/**
 * Default external ID used for identifying asset.
 * @deprecated This value will be removed after migration is complete and external IDs are added in LD for all customers
 */
export const DEFAULT_ASSET_EXTERNAL_ID = 'FLOC'

/** 7 mb */
export const EMAIL_TOTAL_MAX_ATTACHMENT_SIZE_IN_BYTES = 7000000

/**
 * Methane density options for different temperature and pressure standards
 * Used for conversions between mass and volume measurements
 */
export const METHANE_DENSITY_OPTIONS = {
  STP_1_BAR: {
    density: 0.7082593, // kg/m³
    organization: 'IUPAC',
    temperature: '0°C (273.15 K)',
    pressure: '100 kPa (1 bar)',
  },
  STP_CONDITIONS: {
    density: 0.71766905, // kg/m³
    organization: 'NIST',
    temperature: '0°C (273.15 K)',
    pressure: '101.325 kPa (1 atm)',
  },
  NTP_CONDITIONS: {
    density: 0.656, // kg/m³
    organization: 'EPA',
    temperature: '20°C (293.15 K)',
    pressure: '101.325 kPa',
  },
  ISO_6976_STANDARD: {
    density: 0.6799, // kg/m³
    organization: 'EPA / AGA / ISO',
    temperature: '15°C (288.15 K)',
    pressure: '101.325 kPa',
  },
} as const

// Default methane density (matches current METHANE_DENSITY_US of 0.01919 kg/ft³)
export const DEFAULT_METHANE_DENSITY =
  METHANE_DENSITY_OPTIONS.ISO_6976_STANDARD.density // 0.679 kg/m³

/**
 * Convert methane density from kg/m³ to kg/ft³
 */
export const convertMethaneDensityToUS = (densityMetric: number): number => {
  return densityMetric * (1 / 35.3147) // m³ to ft³ conversion
}

/**
 * Methane density at U.S. Oil & Gas standard conditions
 * Reference conditions:
 * - Temperature: 60°F (15.56°C)
 * - Pressure: 14.7 psia (1 atm)
 * These are the standard conditions commonly used in the North American O&G industry
 * for natural gas measurement and calculations.
 * Unit: kg/ft³
 * Note: This uses the REF_TEMP density (0.679 kg/m³) converted to imperial units
 * which matches the historical value of 0.01919 kg/ft³
 */
export const METHANE_DENSITY_US = convertMethaneDensityToUS(
  DEFAULT_METHANE_DENSITY
) // kg/ft³

// Use U.S. O&G standard as default
export const METHANE_DENSITY = METHANE_DENSITY_US

export const TIME_UNIT_LABELS = {
  [TimeUnit.MINUTE]: { short: 'm', long: 'minute' },
  [TimeUnit.HOUR]: { short: 'h', long: 'hour' },
  [TimeUnit.DAY]: { short: 'd', long: 'day' },
} as const

export const UNIT_TEMPLATES = {
  [MeasurementType.VOLUME]: {
    [UnitSystem.IMPERIAL]: {
      unit: 'mscf',
      name: 'Thousand standard cubic feet',
    },
    [UnitSystem.METRIC]: { unit: 'm³', name: 'Cubic meters' },
  },
  [MeasurementType.MASS]: {
    [UnitSystem.IMPERIAL]: { unit: 'lb', name: 'Pounds' },
    [UnitSystem.METRIC]: { unit: 'kg', name: 'Kilograms' },
  },
} as const

export const CONVERSION_FACTORS = {
  // Time conversions (from hour)
  TIME: {
    minute: 1 / 60,
    hour: 1,
    day: 24,
  },
  // Volume conversions (from standard cubic feet)
  VOLUME: {
    scf: 1, // standard cubic feet (base unit)
    mcf: 1 / 1000, // thousand cubic feet
    mmscf: 1 / 1000000, // million standard cubic feet
    bscf: 1 / 1000000000, // billion standard cubic feet
    m3: 0.0283168, // cubic meters
    e3m3: 0.0283168 / 1000, // thousand cubic meters
  },
  // Unit system conversions
  UNIT_SYSTEM: {
    FT3_TO_M3: 0.0283168, // cubic feet to cubic meters
    M3_TO_FT3: 35.3147, // cubic meters to cubic feet
    KG_TO_LB: 2.20462, // kilograms to pounds
    LB_TO_KG: 0.453592, // pounds to kilograms
    get KG_M3_TO_LB_FT3() {
      return this.KG_TO_LB * this.FT3_TO_M3 // kg/m³ to lb/ft³ (calculated)
    },
  },
  // Density unit labels
  DENSITY_UNITS: {
    METRIC: 'kg/m³',
    IMPERIAL: 'lb/ft³',
  },
} as const
